import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Gavel as AuthorityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";

const AuthorityCharges = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [authorityData, setAuthorityData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedAuthorityForDelete, setSelectedAuthorityForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingAuthority, setEditingAuthority] = useState(null);
  const [newAuthority, setNewAuthority] = useState({
    authorityName: '',
    perSqFtCharge: '',
    status: 'Active',
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch authority charges
  const fetchAuthorities = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=authority_charges`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const data = Array.isArray(result.data)
        ? result.data.map((item) => ({
          id: item._id,
          authorityName: item.details?.authorityName || 'Unknown',
          perSqFtCharge: item.details?.perSqFtCharge || 0,
          status: item.status === true ? 'Active' : 'Inactive',
          rawStatus: item.status,
        }))
        : [];

      setAuthorityData(data);
    } catch (err) {
      setApiError('Failed to load authority charges.');
      setAuthorityData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthorities();
  }, []);

  // Filter logic
  const filteredAuthorities = authorityData.filter((a) => {
    const matchesSearch = a.authorityName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const paginatedData = filteredAuthorities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newAuthority.authorityName.trim()) err.authorityName = 'Authority Name is required';
    if (!newAuthority.perSqFtCharge) err.perSqFtCharge = 'Charge is required';
    return err;
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingAuthority(null);
    setNewAuthority({ authorityName: '', perSqFtCharge: '', status: 'Active' });
    setErrors({});
    setOpenModal(true);
  };

  const handleEdit = (authority) => {
    setEditingAuthority(authority);
    setNewAuthority({
      authorityName: authority.authorityName,
      perSqFtCharge: authority.perSqFtCharge,
      status: authority.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'authority_charges',
        details: {
          authorityName: newAuthority.authorityName.trim(),
          perSqFtCharge: parseFloat(newAuthority.perSqFtCharge),
        },
        status: newAuthority.status === 'Active',
      };

      const url = editingAuthority
        ? `${API_BASE}/api/globalMasterDetails/${editingAuthority.id}`
        : `${API_BASE}/api/globalMasterDetails`;

      const response = await fetch(url, {
        method: editingAuthority ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editingAuthority ? 'Updated successfully!' : 'Added successfully!'
      });
      setAlertOpen(true);
      setOpenModal(false);
      fetchAuthorities(true);
    } catch (err) {
      setApiError('Failed to save data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (authority) => {
    setSelectedAuthorityForDelete(authority);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAuthorityForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      await fetch(`${API_BASE}/api/globalMasterDetails/${selectedAuthorityForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });
      setAlertConfig({ title: 'Success', message: 'Authority charge deleted successfully!' });
      setAlertOpen(true);
      fetchAuthorities(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Delete failed.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedAuthorityForDelete(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AuthorityIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Authority Charges Master</Typography>
        </Box>
         <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchAuthorities()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Add Charge
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the authority charge <strong>{selectedAuthorityForDelete?.authorityName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchAuthorities()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Filter Card */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Authority..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
             <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredAuthorities.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} records matching your criteria.` : 'No records found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setPage(0);
                    fetchAuthorities(true);
                  }}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f7fa' }}>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Authority Name</TableCell>
                <TableCell>Rate (Per Sq.Ft)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((auth, index) => (
                <TableRow key={auth.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{auth.authorityName}</TableCell>
                  <TableCell>₹{auth.perSqFtCharge}</TableCell>
                  <TableCell>
                    <Chip label={auth.status} color={auth.status === 'Active' ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    {canWrite && (
                      <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleEdit(auth)}><EditIcon /></IconButton>
                      </Tooltip>
                    )}
                    {canWrite && (
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDeleteClick(auth)}><DeleteIcon /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAuthorities.length}
          page={page}
          onPageChange={(e, n) => setPage(n)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAuthority ? 'Edit Authority' : 'Add Authority'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Authority Name"
                value={newAuthority.authorityName}
                onChange={(e) => setNewAuthority({ ...newAuthority, authorityName: e.target.value })}
                error={!!errors.authorityName}
                helperText={errors.authorityName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Rate Per Sq.Ft" type="number"
                value={newAuthority.perSqFtCharge}
                onChange={(e) => setNewAuthority({ ...newAuthority, perSqFtCharge: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                error={!!errors.perSqFtCharge}
                helperText={errors.perSqFtCharge}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newAuthority.status}
                  label="Status"
                  onChange={(e) => setNewAuthority({ ...newAuthority, status: e.target.value })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
         <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenModal(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
            {editingAuthority ? 'Update' : 'Add'} Charge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuthorityCharges;